import {
  baseApi,
  CONFIG_EMAIL_TAG,
  CONFIG_SMS_TAG,
  CONFIG_BASE_URL_TAG,
  CONFIG_TELEGRAM_TAG,
  CONFIG_TELEGRAM_CHAT_ROUTING_TAG,
  CONFIG_IMAGES_TAG,
  CURRENCIES_TAG,
} from '@/shared';
import {
  configEmailAdapter,
  configBaseUrlAdapter,
  configTelegramAdapter,
  configTelegramChatRoutingAdapter,
} from '../utils/helpers/adapter';

import type {
  TConfigBaseUrl,
  TConfigEmail,
  TConfigSms,
  TConfigTelegram,
  TConfigTelegramChatRouting,
  TGetConfigImagesParams,
  TConfigImagesResponse,
  TConfigUploadImageParams,
  TConfigDeleteImageParams,
  TCreateCurrencyParams,
} from './types';

export const paymentSystemsApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getConfigBaseUrl: build.query<TConfigBaseUrl, void>({
      query: () => ({
        url: '/admin/settings/base_url',
        method: 'GET',
      }),
      transformResponse: configBaseUrlAdapter,
      providesTags: [CONFIG_BASE_URL_TAG],
    }),

    updateConfigBaseUrl: build.mutation<void, TConfigBaseUrl>({
      query: (params: TConfigBaseUrl) => ({
        url: '/admin/settings/base_url',
        method: 'PUT',
        body: {
          base_url: params.baseUrl,
        },
      }),
      invalidatesTags: [CONFIG_BASE_URL_TAG],
    }),

    getConfigEmail: build.query<TConfigEmail, void>({
      query: () => ({
        url: '/admin/settings/email',
        method: 'GET',
      }),
      transformResponse: configEmailAdapter,
      providesTags: [CONFIG_EMAIL_TAG],
    }),

    updateConfigEmail: build.mutation<void, TConfigEmail>({
      query: (params: TConfigEmail) => ({
        url: '/admin/settings/email',
        method: 'PUT',
        body: {
          smtp_address: params.smtpAddress,
          smtp_port: params.smtpPort,
          login: params.login,
          password: params.password,
          email_proxy_url: params.emailProxyUrl,
        },
      }),
      invalidatesTags: [CONFIG_EMAIL_TAG],
    }),

    getConfigSms: build.query<TConfigSms, void>({
      query: () => ({
        url: '/admin/settings/sms',
        method: 'GET',
      }),
      // transformResponse: configSmsAdapter,
      providesTags: [CONFIG_SMS_TAG],
    }),

    updateConfigSms: build.mutation<void, TConfigSms>({
      query: (params: TConfigSms) => ({
        url: '/admin/settings/sms',
        method: 'PUT',
        body: params,
      }),
      invalidatesTags: [CONFIG_SMS_TAG],
    }),

    getConfigTelegram: build.query<TConfigTelegram, void>({
      query: () => ({
        url: '/admin/settings/telegram',
        method: 'GET',
      }),
      transformResponse: configTelegramAdapter,
      providesTags: [CONFIG_TELEGRAM_TAG],
    }),

    updateConfigTelegram: build.mutation<void, TConfigTelegram>({
      query: (params: TConfigTelegram) => ({
        url: '/admin/settings/telegram',
        method: 'PUT',
        body: {
          bot_token: params.botToken,
          deposits_chat_id: params.depositsChatId,
          withdrawals_chat_id: params.withdrawalsChatId,
        },
      }),
      invalidatesTags: [CONFIG_TELEGRAM_TAG],
    }),

    getConfigTelegramChatRouting: build.query<TConfigTelegramChatRouting, void>({
      query: () => ({
        url: '/admin/settings/telegram_chat_routing',
        method: 'GET',
      }),
      transformResponse: configTelegramChatRoutingAdapter,
      providesTags: [CONFIG_TELEGRAM_CHAT_ROUTING_TAG],
    }),

    updateConfigTelegramChatRouting: build.mutation<void, TConfigTelegramChatRouting>({
      query: (params: TConfigTelegramChatRouting) => ({
        url: '/admin/settings/telegram_chat_routing',
        method: 'PUT',
        body: {
          enabled: params.enabled,
          operator_usernames: params.operators.split(/[\s,]+/).filter(Boolean),
          relay_chats: params.relayChats.map((chat) => ({
            name: chat.name,
            chat_id: Number(chat.chatId),
            destination_chat_id: Number(chat.destinationChatId),
            confirmation_reaction: chat.confirmationReaction,
            confirmation_alternate_reaction: chat.confirmationAlternateReaction,
            confirmation_third_reaction: chat.confirmationThirdReaction,
            confirmation_custom_emoji_id: chat.confirmationCustomEmojiId.trim(),
            reminder_usernames: chat.reminderUsernames.split(/[\s,]+/).filter(Boolean),
            confirmation_text: chat.confirmationText,
            success_message: chat.successMessage,
          })),
          client_chats: params.clientChats.map((clientChat) => ({
            name: clientChat.name,
            chat_id: Number(clientChat.chatId),
            shop_ids: clientChat.shopIds.map(Number),
            routes: clientChat.routes.map((route) => ({
              payment_system: route.paymentSystem,
              chat_id: Number(route.chatId),
            })),
            lookup_fields: clientChat.lookupFields,
            missing_reference_message: clientChat.missingReferenceMessage,
            unresolved_message: clientChat.unresolvedMessage,
            success_message: clientChat.successMessage,
          })),
        },
      }),
      invalidatesTags: [CONFIG_TELEGRAM_CHAT_ROUTING_TAG],
    }),

    getConfigImages: build.query<TConfigImagesResponse, TGetConfigImagesParams>({
      query: (params) => ({
        url: '/admin/images/list',
        method: 'GET',
        params: {
          page_size: params.pageSize,
          page: params.page,
          ascending: params.ascending,
        }
      }),
      providesTags: [CONFIG_IMAGES_TAG],
    }),

    uploadConfigImage: build.mutation<void, TConfigUploadImageParams>({
      query: (params: TConfigUploadImageParams) => ({
        url: '/admin/images/upload',
        method: 'POST',
        body: params.body,
      }),
      invalidatesTags: [CONFIG_IMAGES_TAG],
    }),

    deleteConfigImage: build.mutation<void, TConfigDeleteImageParams>({
      query: (params: TConfigDeleteImageParams) => ({
        url: `/admin/images/delete/${params.imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CONFIG_IMAGES_TAG],
    }),

    createCurrency: build.mutation<void, TCreateCurrencyParams>({
      query: (params) => ({
        url: '/admin/currencies/create',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: [CURRENCIES_TAG],
    }),

  }),
});

export const {
  useGetConfigBaseUrlQuery,
  useUpdateConfigBaseUrlMutation,
  useGetConfigEmailQuery,
  useUpdateConfigEmailMutation,
  useGetConfigSmsQuery,
  useUpdateConfigSmsMutation,
  useGetConfigTelegramQuery,
  useUpdateConfigTelegramMutation,
  useGetConfigTelegramChatRoutingQuery,
  useUpdateConfigTelegramChatRoutingMutation,
  useGetConfigImagesQuery,
  useUploadConfigImageMutation,
  useDeleteConfigImageMutation,
  useCreateCurrencyMutation,
} = paymentSystemsApi;
